import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useVariant } from '../contexts/VariantContext';
import { useSEO } from '../contexts/SEOContext';
import { renderCtaButton } from '../utils/ctaHelper';
import { usePageTitle } from '../hooks/usePageTitle';

const ResourceCenter = () => {
  const { brand, domainConfig, cta } = useTheme();
  const { variant } = useVariant();
  const { currentSEO, generateFAQStructuredData } = useSEO();
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  
  // Get hostname-specific H1 title
  const h1Title = usePageTitle('resources');

  // Get resource center data from domain config
  const getResourceCenterData = () => {
    if (domainConfig?.resourceCenter) {
      return domainConfig.resourceCenter;
    }
    
    // Fallback if no resource center configured
    return {
      title: 'Resource Center',
      subtitle: 'Educational resources and information',
      description: 'Explore our comprehensive guides and resources.',
      hero: {
        title: 'Resource Center',
        subtitle: 'Everything you need to know',
        description: 'Comprehensive guides, articles, and resources to help you make informed decisions.'
      },
      sections: [],
      guides: [],
      articles: [],
      faqs: []
    };
  };

  const resourceData = getResourceCenterData();
  
  // Generate FAQ structured data for SEO
  const faqStructuredData = generateFAQStructuredData(resourceData.faqs);

  // Get domain-specific styling based on variant
  const getVariantStyles = () => {
    switch (variant) {
      case 'minimal':
        return {
          container: 'bg-white',
          hero: 'bg-gray-50',
          heroTitle: 'text-gray-900',
          heroSubtitle: 'text-gray-900',
          heroText: 'text-gray-900',
          section: 'bg-white',
          sectionAlt: 'bg-gray-50',
          card: 'bg-white border border-gray-200 hover:shadow-lg transition-shadow duration-300',
          button: 'bg-gray-900 text-white hover:bg-gray-800',
          buttonSecondary: 'border border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50',
          title: 'text-gray-900',
          subtitle: 'text-gray-900',
          text: 'text-gray-900',
          textSecondary: 'text-gray-900',
          icon: 'text-gray-900',
          faqButton: 'bg-gray-50 hover:bg-gray-100 text-gray-900',
          faqAnswer: 'bg-gray-50 text-gray-900',
          badge: 'bg-gray-100 text-gray-800'
        };
      case 'modern':
        return {
          container: 'bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900',
          hero: 'bg-gradient-to-br from-slate-800 via-purple-800 to-slate-800',
          heroTitle: 'text-white',
          heroSubtitle: 'text-white',
          heroText: 'text-white',
          section: 'bg-transparent',
          sectionAlt: 'bg-white/5',
          card: 'bg-white/5 backdrop-blur-md border border-white/10 hover:border-white/20 hover:shadow-xl transition-all duration-300',
          button: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600',
          buttonSecondary: 'border-2 border-white/30 text-white hover:bg-white/10',
          title: 'text-white',
          subtitle: 'text-white',
          text: 'text-white',
          textSecondary: 'text-white',
          icon: 'text-white',
          faqButton: 'bg-white/5 hover:bg-white/10 text-white',
          faqAnswer: 'bg-white/5 text-white',
          badge: 'bg-purple-500/20 text-purple-200'
        };
      case 'clean':
        return {
          container: 'bg-white',
          hero: 'bg-gray-50',
          heroTitle: 'text-gray-900',
          heroSubtitle: 'text-gray-900',
          heroText: 'text-gray-900',
          section: 'bg-white',
          sectionAlt: 'bg-gray-50',
          card: 'bg-white border border-gray-100 hover:shadow-lg transition-all duration-300',
          button: 'bg-gray-900 text-white hover:bg-gray-800',
          buttonSecondary: 'border border-gray-300 text-gray-600 hover:border-gray-400',
          title: 'text-gray-900',
          subtitle: 'text-gray-900',
          text: 'text-gray-900',
          textSecondary: 'text-gray-900',
          icon: 'text-gray-900',
          faqButton: 'bg-white hover:bg-gray-50 text-gray-900',
          faqAnswer: 'bg-gray-50 text-gray-900',
          badge: 'bg-gray-100 text-gray-700'
        };
      case 'corporate':
        return {
          container: 'bg-gray-50',
          hero: 'bg-gradient-to-r from-blue-600 to-indigo-700',
          heroTitle: 'text-white',
          heroSubtitle: 'text-white',
          heroText: 'text-white',
          section: 'bg-white',
          sectionAlt: 'bg-gray-50',
          card: 'bg-white shadow-md hover:shadow-lg transition-shadow duration-300',
          button: 'bg-indigo-600 text-white hover:bg-indigo-700',
          buttonSecondary: 'border border-indigo-200 text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50',
          title: 'text-gray-900',
          subtitle: 'text-gray-900',
          text: 'text-gray-900',
          textSecondary: 'text-gray-900',
          icon: 'text-gray-900',
          faqButton: 'bg-gray-50 hover:bg-gray-100 text-gray-900',
          faqAnswer: 'bg-indigo-50 text-gray-900',
          badge: 'bg-indigo-100 text-indigo-800'
        };
      case 'landing':
        return {
          container: 'bg-gradient-to-br from-blue-50 to-indigo-100',
          hero: 'bg-gradient-to-r from-blue-600 to-indigo-600',
          heroTitle: 'text-white',
          heroSubtitle: 'text-white',
          heroText: 'text-white',
          section: 'bg-white',
          sectionAlt: 'bg-gradient-to-br from-blue-50 to-indigo-100',
          card: 'bg-white shadow-xl hover:shadow-2xl transition-all duration-300',
          button: 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700',
          buttonSecondary: 'border border-blue-300 text-blue-600 hover:border-blue-400 hover:bg-blue-50',
          title: 'text-gray-900',
          subtitle: 'text-gray-900',
          text: 'text-gray-900',
          textSecondary: 'text-gray-900',
          icon: 'text-gray-900',
          faqButton: 'bg-white hover:bg-blue-50 text-gray-900',
          faqAnswer: 'bg-blue-50 text-gray-900',
          badge: 'bg-blue-100 text-blue-800'
        };
      case 'grid':
        return {
          container: 'bg-white',
          hero: 'bg-gradient-to-r from-[#195A8E] to-[#144A73]',
          heroTitle: 'text-white',
          heroSubtitle: 'text-white',
          heroText: 'text-white',
          section: 'bg-white',
          sectionAlt: 'bg-gray-50',
          card: 'bg-white border-2 border-gray-100 hover:border-gray-300 hover:shadow-lg transition-all duration-300',
          button: 'bg-[#195A8E] text-white hover:bg-[#144A73]',
          buttonSecondary: 'border border-[#195A8E] text-[#195A8E] hover:bg-[#195A8E] hover:text-white',
          title: 'text-gray-900',
          subtitle: 'text-gray-900',
          text: 'text-gray-900',
          textSecondary: 'text-gray-900',
          icon: 'text-gray-900',
          faqButton: 'bg-gray-50 hover:bg-gray-100 text-gray-900',
          faqAnswer: 'bg-gray-50 text-gray-900',
          badge: 'bg-blue-100 text-blue-800'
        };
      case 'creative':
        return {
          container: 'bg-gradient-to-br from-orange-100 via-pink-100 to-purple-100',
          hero: 'bg-gradient-to-r from-orange-500 to-pink-500',
          heroTitle: 'text-white',
          heroSubtitle: 'text-white',
          heroText: 'text-white',
          section: 'bg-white/80 backdrop-blur-sm',
          sectionAlt: 'bg-white/50',
          card: 'bg-white/80 backdrop-blur-sm border border-white/50 shadow-lg hover:shadow-xl transition-all duration-300',
          button: 'bg-gradient-to-r from-orange-500 to-pink-500 text-white hover:from-orange-600 hover:to-pink-600',
          buttonSecondary: 'border border-orange-300 text-orange-600 hover:border-orange-400 hover:bg-orange-50',
          title: 'text-gray-900',
          subtitle: 'text-gray-900',
          text: 'text-gray-900',
          textSecondary: 'text-gray-900',
          icon: 'text-gray-900',
          faqButton: 'bg-white hover:bg-orange-50 text-gray-900',
          faqAnswer: 'bg-orange-50 text-gray-900',
          badge: 'bg-orange-100 text-orange-800'
        };
      case 'magazine':
        return {
          container: 'bg-white',
          hero: 'bg-gray-900',
          heroTitle: 'text-white',
          heroSubtitle: 'text-white',
          heroText: 'text-white',
          section: 'bg-white',
          sectionAlt: 'bg-gray-50',
          card: 'bg-white border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300',
          button: 'bg-gray-900 text-white hover:bg-gray-800',
          buttonSecondary: 'border border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50',
          title: 'text-gray-900',
          subtitle: 'text-gray-900',
          text: 'text-gray-900',
          textSecondary: 'text-gray-900',
          icon: 'text-gray-900',
          faqButton: 'bg-gray-50 hover:bg-gray-100 text-gray-900',
          faqAnswer: 'bg-gray-50 text-gray-900',
          badge: 'bg-gray-200 text-gray-800'
        };
      case 'professional':
        return {
          container: 'bg-slate-50',
          hero: 'bg-blue-900',
          heroTitle: 'text-white',
          heroSubtitle: 'text-white',
          heroText: 'text-white',
          section: 'bg-white',
          sectionAlt: 'bg-slate-50',
          card: 'bg-white border border-slate-200 shadow-lg hover:shadow-xl transition-all duration-300',
          button: 'bg-blue-600 text-white hover:bg-blue-700',
          buttonSecondary: 'border border-slate-300 text-slate-700 hover:border-slate-400 hover:bg-slate-50',
          title: 'text-slate-900',
          subtitle: 'text-slate-900',
          text: 'text-slate-900',
          textSecondary: 'text-slate-900',
          icon: 'text-slate-900',
          faqButton: 'bg-slate-50 hover:bg-slate-100 text-slate-900',
          faqAnswer: 'bg-blue-50 text-slate-900',
          badge: 'bg-blue-100 text-blue-800'
        };
      case 'elegant':
        return {
          container: 'bg-gradient-to-br from-blue-50 via-white to-blue-100',
          hero: 'bg-gradient-to-r from-blue-800 to-blue-900',
          heroTitle: 'text-white',
          heroSubtitle: 'text-white',
          heroText: 'text-white',
          section: 'bg-white/80 backdrop-blur-sm',
          sectionAlt: 'bg-white/50',
          card: 'bg-white/80 backdrop-blur-sm border border-blue-200/50 shadow-sm hover:shadow-lg transition-all duration-300',
          button: 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800',
          buttonSecondary: 'border border-blue-300 text-blue-700 hover:border-blue-400 hover:bg-blue-50',
          title: 'text-blue-900',
          subtitle: 'text-blue-900',
          text: 'text-blue-900',
          textSecondary: 'text-blue-900',
          icon: 'text-blue-900',
          faqButton: 'bg-white hover:bg-blue-50 text-blue-900',
          faqAnswer: 'bg-blue-50 text-blue-900',
          badge: 'bg-blue-100 text-blue-800'
        };
      case 'dark':
        return {
          container: 'bg-gray-900',
          hero: 'bg-gradient-to-r from-gray-800 to-gray-900',
          heroTitle: 'text-white',
          heroSubtitle: 'text-white',
          heroText: 'text-white',
          section: 'bg-transparent',
          sectionAlt: 'bg-gray-800/50',
          card: 'bg-white/5 backdrop-blur-md border border-white/10 hover:border-white/20 hover:shadow-lg transition-all duration-300',
          button: 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700',
          buttonSecondary: 'border border-white/30 text-white hover:bg-white/10',
          title: 'text-white',
          subtitle: 'text-white',
          text: 'text-white',
          textSecondary: 'text-white',
          icon: 'text-white',
          faqButton: 'bg-white/5 hover:bg-white/10 text-white',
          faqAnswer: 'bg-white/5 text-white',
          badge: 'bg-purple-500/20 text-purple-200'
        };
      default:
        return {
          container: 'bg-white',
          hero: 'bg-blue-600',
          heroTitle: 'text-white',
          heroSubtitle: 'text-white',
          heroText: 'text-white',
          section: 'bg-white',
          sectionAlt: 'bg-gray-50',
          card: 'bg-white shadow-md hover:shadow-lg transition-shadow duration-300',
          button: 'bg-blue-600 text-white hover:bg-blue-700',
          buttonSecondary: 'border border-blue-200 text-blue-600 hover:border-blue-300 hover:bg-blue-50',
          title: 'text-gray-900',
          subtitle: 'text-gray-900',
          text: 'text-gray-900',
          textSecondary: 'text-gray-900',
          icon: 'text-gray-900',
          faqButton: 'bg-gray-50 hover:bg-gray-100 text-gray-900',
          faqAnswer: 'bg-blue-50 text-gray-900',
          badge: 'bg-blue-100 text-blue-800'
        };
    }
  };

  const styles = getVariantStyles();

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <div className={`min-h-screen ${styles.container}`}>
      {/* FAQ Structured Data for SEO */}
      {faqStructuredData && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(faqStructuredData)}
          </script>
        </Helmet>
      )}

      {/* Hero Section */}
      <section className={`${styles.hero} pt-32 pb-20`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h1 className={`text-5xl lg:text-6xl font-bold mb-6 ${styles.heroTitle}`}>
              {h1Title || resourceData.hero?.title || resourceData.title}
            </h1>
            <p className={`text-xl lg:text-2xl mb-8 max-w-3xl mx-auto ${styles.heroSubtitle}`}>
              {resourceData.hero?.subtitle || resourceData.subtitle}
            </p>
            {resourceData.hero?.description && (
              <p className={`text-lg max-w-4xl mx-auto mb-8 ${styles.heroText}`}>
                {resourceData.hero.description}
              </p>
            )}
            {resourceData.showComparePrices && (
              <div className="mt-8">
                <Link
                  to="/compare-prices"
                  className={`inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-200 ${styles.button}`}
                >
                  Compare Tailored Prices
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Educational Sections */}
      {resourceData.sections && resourceData.sections.length > 0 && (
        <section className={`py-20 ${styles.sectionAlt}`}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className={`text-3xl lg:text-4xl font-bold mb-4 ${styles.title}`}>
                Get Started
              </h2>
              <p className={`text-lg max-w-3xl mx-auto ${styles.subtitle}`}>
                Explore some of our in-depth guides and find the best solution for your business.
              </p>
            </div>
            {resourceData.sections.map((section, index) => (
              <div key={index} className={`mb-16 ${index === resourceData.sections.length - 1 ? 'mb-0' : ''}`}>
                <div className={`rounded-2xl p-8 lg:p-12 ${styles.card}`}>
                  <h3 className={`text-2xl lg:text-3xl font-bold mb-4 ${styles.title}`}>
                    {section.title}
                  </h3>
                  <p className={`text-lg mb-6 ${styles.text}`}>
                    {section.description}
                  </p>
                  {section.items && section.items.length > 0 && (
                    <ul className={`space-y-3 ${styles.text}`}>
                      {section.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start">
                          <span className={`mr-3 mt-1 ${styles.icon}`}>•</span>
                          <span className="text-lg">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Benefits Section */}
      {resourceData.benefits && resourceData.benefits.length > 0 && (
        <section className={`py-20 ${styles.sectionAlt}`}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className={`text-3xl lg:text-4xl font-bold mb-4 ${styles.title}`}>
                {resourceData.benefitsSection?.title || resourceData.benefitsTitle || 'Benefits of Dash Cams'}
              </h2>
              <p className={`text-lg max-w-3xl mx-auto ${styles.subtitle}`}>
                {resourceData.benefitsSection?.description || resourceData.benefitsDescription || 'Discover how dash cams can transform your fleet operations'}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {resourceData.benefits.map((benefit, index) => (
                <div key={index} className={`rounded-xl p-6 ${styles.card}`}>
                  <h3 className={`text-xl font-bold mb-3 ${styles.title}`}>
                    {benefit.icon} {benefit.title}
                  </h3>
                  <p className={styles.text}>
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {resourceData.faqs && resourceData.faqs.length > 0 && (
        <section className={`py-20 ${styles.section}`}>
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className={`text-3xl lg:text-4xl font-bold mb-4 ${styles.title}`}>
                Frequently Asked Questions
              </h2>
              <p className={`text-lg ${styles.subtitle}`}>
                Get answers to common questions
              </p>
            </div>
            <div className="space-y-4">
              {resourceData.faqs.map((faq, index) => (
                <div key={index} className={`rounded-xl overflow-hidden ${styles.card}`}>
                  <button
                    onClick={() => toggleFaq(index)}
                    className={`w-full text-left px-6 py-5 flex items-center justify-between transition-colors duration-200 ${styles.faqButton}`}
                  >
                    <span className={`font-semibold text-lg pr-8 ${styles.title}`}>
                      {faq.question}
                    </span>
                    <span className={`text-2xl flex-shrink-0 ${styles.icon}`}>
                      {openFaqIndex === index ? '−' : '+'}
                    </span>
                  </button>
                  {openFaqIndex === index && (
                    <div className={`px-6 py-5 ${styles.faqAnswer}`}>
                      <p className={`leading-relaxed whitespace-pre-line ${styles.text}`}>
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className={`py-20 ${styles.sectionAlt}`}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className={`text-3xl lg:text-4xl font-bold mb-6 ${styles.title}`}>
            Ready to Get Started?
          </h2>
          <p className={`text-lg mb-8 ${styles.subtitle}`}>
            Contact our experts to discuss how our solutions can help protect your business and improve operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {renderCtaButton(cta?.primary, 'Request Demo', `inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg transition-colors duration-200 ${styles.button}`, domainConfig?.shopLink)}
            {renderCtaButton(cta?.secondary, 'Contact Us', `inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg transition-colors duration-200 ${styles.buttonSecondary}`, domainConfig?.shopLink)}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResourceCenter;

